import { Component, computed, EventEmitter, linkedSignal, Output, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SharedService } from 'src/app/services/shared.service';
import { Size, Sizes } from 'src/app/models/types/Product/Product.type';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { BuildEndPayload, StrapDTO } from 'src/app/models/types/Strap/Strap.type';
import { StockButtonComponent } from '../../product-page/view-stock/stock-button/stock-button.component';
import { AuthService } from 'src/app/services/auth.service';
import { Price } from 'src/app/models/types/Product/Price.type';
import { AreasWithStrap, AreaWithStrap, CompaniesWithStraps, CompanyWithStraps, GlobalArea, GlobalAreaDTO, StockCounters, StoreStock } from 'src/app/models/types/Product/Stock.type';
import { LoaderService } from 'src/app/services/loader.service';
import { StockAndPriceService } from 'src/app/services/api/stock-and-price.service';
import { ToastService } from 'src/app/services/toast.service';
import { MyStoreStockComponent } from '../../product-page/view-stock/my-store-stock/my-store-stock.component';
import { PriceListModalComponent } from '../price-list-modal/price-list-modal.component';
import { AccordionDirective } from 'src/app/directives/accordion.directive';
import { SharedModule } from 'src/app/modules/shared.module';

@Component({
  selector: 'app-straps-modal',
  imports: [
    PriceListModalComponent,
    MyStoreStockComponent,
    StockButtonComponent,
    ModalComponent,
    AccordionDirective,
    SharedModule
  ],
  templateUrl: './straps-modal.component.html',
  styleUrl: './straps-modal.component.scss'
})
export class StrapsModalComponent {

  @ViewChild('modal', { static: false }) modal!: ModalComponent;
  @ViewChild('priceListModal', { static: false }) priceListModal!: PriceListModalComponent;

  @Output('onBuildEnd') onBuildEnd: EventEmitter<BuildEndPayload> = new EventEmitter();

  public readonly _EMPTY_STOCK_COUNTERS: StockCounters = { free: 0, reservation: 0, consignmentIn: 0, inTransit: 0 };

  public strapCode: string = '';

  private _strap: WritableSignal<StrapDTO | null> = signal<StrapDTO | null>(null);
  public strap: Signal<StrapDTO | null> = this._strap.asReadonly();
  public get showStrap(): boolean {
    if (this.authService.user?.isUserGlobalHQ) {
      return this.selectedCompanyIndex() !== null && this.strap() !== null && !this.storeStock();
    }
    return this.strap() !== null && !this.storeStock();
  }

  private _storeStock: WritableSignal<StoreStock | null> = signal<StoreStock | null>(null);
  public storeStock: Signal<StoreStock | null> = this._storeStock.asReadonly();
  public get showStoreStock(): boolean {
    return this.storeStock() !== null;
  }

  private _globalAreas: WritableSignal<AreasWithStrap | null> = signal<AreasWithStrap | null>(null);
  public globalAreas: Signal<AreasWithStrap | null> = this._globalAreas.asReadonly();
  public selectedAreaIndex: WritableSignal<number | null> = signal<number | null>(null);
  public selectedAreaCompaniesWithStrap: Signal<CompaniesWithStraps | null[] | null> = computed(() => {
    let area = this.globalAreas();
    let areaIndex = this.selectedAreaIndex();
    if (area && areaIndex !== null) {
      return area[areaIndex].companiesWithStrap;
    }
    return null;
  })
  public get showGlobalAreas(): boolean {
    return Boolean(this.authService.user?.isUserGlobalHQ && this.selectedAreaIndex() === null);
  }
  public selectedCompanyIndex: WritableSignal<number | null> = linkedSignal<number | null>(() => {
    let selectedAreaIndex = this.selectedAreaIndex();
    return null;
  })
  public get showCompanies(): boolean {
    return Boolean(this.authService.user?.isUserGenerallyHQ && this.selectedAreaIndex() !== null && this.selectedCompanyIndex() === null);
  }

  public price: Signal<Price | null> = computed(() => this.getPrice(this._strap()));

  public get showNoStorePlaceholder(): boolean {
    return this.showStrap && this.strap()?.stock.storeStocks.length === 0;
  }

  public get showBackButton(): boolean {
    return this.showStoreStock;
  }

  constructor(
    public readonly authService: AuthService,
    private readonly toastService: ToastService,
    public readonly sharedService: SharedService,
    private readonly loaderService: LoaderService,
    private readonly stockAndPriceService: StockAndPriceService,
  ) { }

  openModal(strapCode: string) {
    this.strapCode = strapCode;
    switch (true) {
      case this.authService.user?.isUserGlobalHQ: {
        this.loadGlobalAreas();
        break;
      }
      case this.authService.user?.isUserHQ: {
        this.loadGlobalAreas(true);
        break;
      }
      default: {
        this.loadStraps(true);
        break;
      }
    }
  }

  loadStraps(needToOpenTheModal?: boolean) {
    if (!this.authService.user?.isUserGenerallyHQ) {
      this.loaderService.show();
    }
    this.stockAndPriceService.searchStraps(this.strapCode).subscribe({
      next: (response) => {
        this._strap.set(response);
        this.modal.dismiss();
        if (needToOpenTheModal) {
          this.buildModal();
        }
        if (!this.authService.user?.isUserGenerallyHQ) {
          this.loaderService.hide();
        }
        this.onBuildEnd.emit({ success: true })
      },
      error: (error) => {
        console.error(error);
        let message = error?.error;
        if (!this.authService.user?.isUserGenerallyHQ) {
          this.loaderService.hide();
        }
        this.onBuildEnd.emit({
          success: false,
          message: message ? message.title : ''
        })
      }
    });
  }

  loadGlobalAreas(forLocalHq?: boolean) {
    if (forLocalHq) {
      const companies = this.authService.companyCodes?.map(c => ({
        companyCode: c.code,
        companyName: c.name,
        country: c.country
      })) || [];
      let fakeArea: AreaWithStrap = {
        companies: companies,
        companiesWithStrap: companies?.map(company => {
          let companyWithStraps: CompanyWithStraps = {
            code: company.companyCode || '',
            name: company.companyName || '',
            country: company.country || '',
            strap: undefined,
            stockCounters: this._EMPTY_STOCK_COUNTERS
          }
          return companyWithStraps
        }) || [],
        countCompanies: companies.length,
        description: '---'
      }
      this._globalAreas.set([fakeArea])
      if (fakeArea.companies?.length === 0) {
        this.onBuildEnd.emit({
          success: false,
          message: "An error occurred"
        });
      } else {
        this.selectArea(0);
        this.buildModal();
        this.onBuildEnd.emit({ success: true });
      }
    } else {
      // this.loaderService.show();
      this.stockAndPriceService.getGlobalAreas().subscribe({
        next: (res) => {
          this._globalAreas.set(res.map(area => ({
            ...area,
            // companiesWithStrap: new Array(area.companies?.length).fill(null)
            companiesWithStrap: area.companies?.map(company => {
              let companyWithStraps: CompanyWithStraps = {
                code: company.companyCode || '',
                name: company.companyName || '',
                country: company.country || '',
                strap: undefined,
                stockCounters: this._EMPTY_STOCK_COUNTERS
              }
              return companyWithStraps
            }) || []
          })));
          this.buildModal();
          // this.loaderService.hide();
          this.onBuildEnd.emit({ success: true })
        },
        error: (err) => {
          console.log(err);
          // this.loaderService.hide();
        }
      });
    }
  }

  getPrice(strap: StrapDTO | null): Price | null {
    const isNotUserStore = this.authService.user?.isNotUserStore;
    const store = isNotUserStore ? this.authService.getDefaultStore() : this.authService.store;
    let price = strap?.prices.find(p => p.priceListCode === store?.priceListCode);
    // let price = strap.prices.find(p => p.priceListCode === store?.priceListCode && p.myPriceList);
    return price || null;
  }

  selectStore(store: StoreStock) {
    this._storeStock.set(store);
  }

  showPriceList() {
    this.priceListModal.openModal(this.strap()?.prices || []);
  }

  handleGoBack() {
    switch (true) {
      case this.showStoreStock: {
        this._storeStock.set(null);
        break;
      }
      case this.showGlobalAreas: {
        break;
      }
      case this.showCompanies: {
        this.selectedAreaIndex.set(null);
        this.selectedCompanyIndex.set(null);
        break;
      }
      case this.showStrap: {
        this.selectedCompanyIndex.set(null);
        this._strap.set(null);
        break;
      }
    }
  }

  selectArea(areaIndex: number) {
    this.selectedAreaIndex.set(areaIndex);
    let area = this.globalAreas()![areaIndex];
    area.companiesWithStrap.forEach((company, index) => {
      if (company?.strap === undefined) {
        this.loadStrapForCompany(
          company?.code || '',
          (strap?: StrapDTO) => {
            this.updateArea(areaIndex, index, strap);
          });
      }
    })
  }

  updateArea(areaIndex: number, companyIndex: number, strap?: StrapDTO) {
    this._globalAreas.update(areas => {
      let counters: StockCounters = this._EMPTY_STOCK_COUNTERS;
      if (strap) {
        counters = this.stockAndPriceService.getStoreStockCounterTotal(strap!.stock.storeStocks);
      }
      areas![areaIndex].companiesWithStrap[companyIndex]!.strap = strap || null;
      areas![areaIndex].companiesWithStrap[companyIndex]!.stockCounters = counters;
      return areas;
    })
  }

  selectCompany(companyIndex: number) {
    this.selectedCompanyIndex.set(companyIndex);
    this._strap.set(this.selectedAreaCompaniesWithStrap()![companyIndex]?.strap || null);
  }

  loadStrapForCompany(companyCode: string, callback: Function) {
    this.loaderService.show();
    this.stockAndPriceService.searchStraps(this.strapCode, companyCode).subscribe({
      next: (response) => {
        callback(response);//Setto lo strap nella company corrispondente
        this.loaderService.hide();
      },
      error: (error) => {
        console.error(error);
        callback();//Setto a null strap
        this.loaderService.hide();
      }
    });
  }

  private buildModal() {
    this.modal.buildModal(() => this.handleClose(), 'lg', 'strap-modal');
  }

  private handleClose() {
    this._strap.set(null);
    this._storeStock.set(null);
    this._globalAreas.set(null);
    this.selectedAreaIndex.set(null);
    this.modal.dismiss();
  }
}


let mock = {
  "stock": {
    "sapcode": "100100101",
    "storeStocks": [
      {
        "errors": [],
        "hasError": false,
        "store": "060",
        "storeName": "ROME CONDOTTI",
        "companyCode": null,
        "idStore": 19,
        "myStore": true,
        "s4Stock": true,
        "mainStock": {
          "free": 10,
          "reservation": 0,
          "consignment": 0,
          "qualityInspection": 0,
          "toVendor": 0,
          "blocked": 0
        },
        "omniChannel": {
          "free": 0
        },
        "consignmentIn": {
          "free": 0,
          "consignmentOut": 0
        },
        "inTransit": [
          {
            "from": "No Incoming in store 060 - ROME CONDOTTI",
            "shippingDate": null,
            "quantity": 0
          }
        ]
      }
    ],
    "showInTransit": false
  },
  "details": "BVLGARI BVLGARI OLD",
  "description": "CLASP YEL GL BB23-26",
  "price": 1200,
  "currency": "EUR",
  "prices": [
    {
      "priceList": "Italy",
      "priceListCode": "ZI",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": true,
      "tax": true
    },
    {
      "priceList": "Australia",
      "priceListCode": "ZA",
      "eventPriceList": false,
      "price": 1800,
      "currency": "AUD",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Bahrain",
      "priceListCode": "Z9",
      "eventPriceList": false,
      "price": 455,
      "currency": "BHD",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Base Price EUR",
      "priceListCode": "Z3",
      "eventPriceList": false,
      "price": 992.56,
      "currency": "EUR",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Belgium",
      "priceListCode": "ZB",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Canada",
      "priceListCode": "Q2",
      "eventPriceList": false,
      "price": 1450,
      "currency": "CAD",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "China",
      "priceListCode": "ZN",
      "eventPriceList": false,
      "price": 9000,
      "currency": "CNY",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Czech Republic",
      "priceListCode": "Q4",
      "eventPriceList": false,
      "price": 29900,
      "currency": "CZK",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Denmark",
      "priceListCode": "QB",
      "eventPriceList": false,
      "price": 9200,
      "currency": "DKK",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "France",
      "priceListCode": "ZF",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Germany",
      "priceListCode": "ZD",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Hawaii",
      "priceListCode": "ZW",
      "eventPriceList": false,
      "price": 1100,
      "currency": "USD",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Hong Kong",
      "priceListCode": "ZH",
      "eventPriceList": false,
      "price": 9000,
      "currency": "HKD",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "India",
      "priceListCode": "ZJ",
      "eventPriceList": false,
      "price": 106000,
      "currency": "INR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Japan",
      "priceListCode": "ZG",
      "eventPriceList": false,
      "price": 173800,
      "currency": "JPY",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Korea",
      "priceListCode": "ZR",
      "eventPriceList": false,
      "price": 1580000,
      "currency": "KRW",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Kuwait",
      "priceListCode": "Z5",
      "eventPriceList": false,
      "price": 350,
      "currency": "KWD",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Macao",
      "priceListCode": "ZP",
      "eventPriceList": false,
      "price": 9300,
      "currency": "MOP",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Malaysia",
      "priceListCode": "ZY",
      "eventPriceList": false,
      "price": 5100,
      "currency": "MYR",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Mexico Cancun",
      "priceListCode": "Q3",
      "eventPriceList": false,
      "price": 23500,
      "currency": "MXN",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Mexico Santa Fe",
      "priceListCode": "Q5",
      "eventPriceList": false,
      "price": 23500,
      "currency": "MXN",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Montecarlo",
      "priceListCode": "ZM",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Netherlands",
      "priceListCode": "Z6",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "New Zealand",
      "priceListCode": "QN",
      "eventPriceList": false,
      "price": 2000,
      "currency": "NZD",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Philippines",
      "priceListCode": "Q9",
      "eventPriceList": false,
      "price": 68000,
      "currency": "PHP",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Portugal",
      "priceListCode": "Q8",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Puerto Rico",
      "priceListCode": "Q7",
      "eventPriceList": false,
      "price": 1100,
      "currency": "USD",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Qatar",
      "priceListCode": "ZQ",
      "eventPriceList": false,
      "price": 4100,
      "currency": "QAR",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Russia",
      "priceListCode": "Q6",
      "eventPriceList": false,
      "price": 123000,
      "currency": "RUB",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Saudi Arabia",
      "priceListCode": "QD",
      "eventPriceList": false,
      "price": 4700,
      "currency": "SAR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Singapore",
      "priceListCode": "ZS",
      "eventPriceList": false,
      "price": 1600,
      "currency": "SGD",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Singapore Apt",
      "priceListCode": "QS",
      "eventPriceList": false,
      "price": 1467.89,
      "currency": "SGD",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Spain",
      "priceListCode": "ZE",
      "eventPriceList": false,
      "price": 1200,
      "currency": "EUR",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "St. Barth",
      "priceListCode": "ZX",
      "eventPriceList": false,
      "price": 1000,
      "currency": "EUR",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Switzerland",
      "priceListCode": "ZC",
      "eventPriceList": false,
      "price": 1000,
      "currency": "CHF",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Taiwan",
      "priceListCode": "ZT",
      "eventPriceList": false,
      "price": 35400,
      "currency": "TWD",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Thailand",
      "priceListCode": "ZL",
      "eventPriceList": false,
      "price": 40800,
      "currency": "THB",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Turkey",
      "priceListCode": "Z8",
      "eventPriceList": false,
      "price": 41200,
      "currency": "TRY",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "UAE",
      "priceListCode": "Q1",
      "eventPriceList": false,
      "price": 4200,
      "currency": "AED",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "United Kingdom",
      "priceListCode": "ZK",
      "eventPriceList": false,
      "price": 1050,
      "currency": "GBP",
      "myPriceList": false,
      "tax": true
    },
    {
      "priceList": "Usa",
      "priceListCode": "ZU",
      "eventPriceList": false,
      "price": 1100,
      "currency": "USD",
      "myPriceList": false,
      "tax": false
    },
    {
      "priceList": "Vietnam",
      "priceListCode": "QC",
      "eventPriceList": false,
      "price": 28960000,
      "currency": "VND",
      "myPriceList": false,
      "tax": true
    }
  ]
}

